import { useState } from 'react';
import { Link } from 'react-router-dom';
import { SITE_LOGO } from '../../../constants/assets';

export type BackofficeTab = 'dashboard' | 'armario' | 'calendario' | 'combinaciones' | 'historial' | 'looks-sugeridos';

interface ClienteInfo {
  id: number;
  nombre: string;
  plan_contratado?: string | null;
}

interface SidebarProps {
  activeTab: BackofficeTab;
  onChange: (tab: BackofficeTab) => void;
  badges?: Partial<Record<BackofficeTab, number>>;
  cliente?: ClienteInfo | null;
  clientes?: ClienteInfo[];
  onChangeCliente?: (id: number) => void;
}

const tabs: { key: BackofficeTab; label: string; icon: string }[] = [
  { key: 'dashboard', label: 'Mi Perfil', icon: 'ri-user-star-line' },
  { key: 'looks-sugeridos', label: 'Looks sugeridos', icon: 'ri-t-shirt-air-line' },
  { key: 'armario', label: 'Mi Armario', icon: 'ri-t-shirt-line' },
  { key: 'calendario', label: 'Calendario', icon: 'ri-calendar-event-line' },
  { key: 'historial', label: 'Looks Usados', icon: 'ri-camera-line' },
  { key: 'combinaciones', label: 'Combinaciones', icon: 'ri-magic-line' },
];

export default function Sidebar({ activeTab, onChange, badges, cliente, clientes, onChangeCliente }: SidebarProps) {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <>
      {/* Mobile header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-brand-gray-200 px-4 h-14 flex items-center justify-between">
        <span className="font-serif text-brand-black text-lg">Mi Asesoría</span>
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="w-10 h-10 flex items-center justify-center text-brand-black"
          aria-label="Menú"
        >
          <i className={mobileOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} />
        </button>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed lg:sticky top-0 lg:top-0 left-0 h-screen w-64 bg-white border-r border-brand-gray-200 flex flex-col z-30 transition-transform duration-300 ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <div className="px-6 pt-8 pb-6 hidden lg:block">
          <Link to="/" className="flex items-center gap-3">
            <img
              src={SITE_LOGO}
              alt="Anthony Sánchez"
              className="h-10 w-auto object-contain"
            />
          </Link>
          <p className="font-sans text-brand-gray-400 text-xs mt-3 tracking-wide">
            Espacio exclusivo
          </p>
        </div>

        <nav className="flex-1 px-3 py-4 lg:py-2 space-y-1">
          {tabs.map((tab) => {
            const badgeCount = badges?.[tab.key] ?? 0;
            return (
              <button
                key={tab.key}
                onClick={() => {
                  onChange(tab.key);
                  setMobileOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl font-sans text-sm transition-colors ${
                  activeTab === tab.key
                    ? 'bg-brand-black text-white'
                    : 'text-brand-gray-600 hover:bg-brand-gray-50 hover:text-brand-black'
                }`}
              >
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className={tab.icon} />
                </div>
                <span className="flex-1 text-left">{tab.label}</span>
                {badgeCount > 0 && (
                  <span className={`inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full font-sans text-[10px] font-medium ${
                    activeTab === tab.key
                      ? 'bg-white text-brand-black'
                      : 'bg-brand-black text-white'
                  }`}>
                    {badgeCount > 99 ? '99+' : badgeCount}
                  </span>
                )}
              </button>
            );
          })}
        </nav>

        <div className="px-6 py-6 border-t border-brand-gray-200">
          {/* Selector de cliente */}
          {clientes && clientes.length > 1 && onChangeCliente && (
            <div className="mb-4">
              <label className="font-sans text-brand-gray-500 text-[10px] uppercase tracking-wider font-medium">
                Cliente
              </label>
              <div className="relative mt-1.5">
                <select
                  value={cliente?.id ?? ''}
                  onChange={(e) => onChangeCliente(Number(e.target.value))}
                  className="w-full appearance-none bg-white border border-brand-gray-200 rounded-lg pl-3 pr-8 py-2 font-sans text-sm focus:outline-none focus:border-brand-black cursor-pointer"
                >
                  {clientes.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.nombre}
                    </option>
                  ))}
                </select>
                <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-brand-gray-400">
                  <i className="ri-arrow-down-s-line" />
                </div>
              </div>
            </div>
          )}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full overflow-hidden bg-brand-gray-100 flex items-center justify-center text-brand-black font-sans text-sm font-bold">
              {cliente?.nombre?.charAt(0)?.toUpperCase() ?? 'C'}
            </div>
            <div className="min-w-0">
              <p className="font-sans text-brand-black text-sm font-medium truncate">
                {cliente?.nombre ?? 'Cliente'}
              </p>
              <p className="font-sans text-brand-gray-400 text-xs truncate">
                {cliente?.plan_contratado ?? 'Sin plan'}
              </p>
            </div>
          </div>
          <Link
            to="/asesoria-imagen"
            className="mt-4 block text-center font-sans text-brand-gray-500 text-xs hover:text-brand-black transition-colors"
          >
            Volver al sitio
          </Link>
        </div>
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-20 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}
    </>
  );
}
